
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { FileText, ArrowLeft, Clock, Database, Sparkles, BarChart3, LayoutDashboard, Download } from 'lucide-react';
import type { WorkflowRun } from '@/hooks/useWorkflowExecution';
import { WorkflowDataViewer } from './WorkflowDataViewer';
import { WorkflowCleaningReport } from './WorkflowCleaningReport';
import { WorkflowPredictionViewer } from './WorkflowPredictionViewer';
import { WorkflowDashboardTab } from './WorkflowDashboardTab';
import { WorkflowOutputFiles } from './WorkflowOutputFiles';
import { formatDateTime } from '@/lib/format';

interface WorkflowRunInspectorProps {
  runId: string;
  onBack: () => void;
}

export const WorkflowRunInspector = ({ runId, onBack }: WorkflowRunInspectorProps) => {
  const { data: run, isLoading } = useQuery({
    queryKey: ['workflow-run', runId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflow_runs')
        .select('*')
        .eq('id', runId)
        .single();
      if (error) throw error;
      return data as WorkflowRun;
    },
  });

  if (isLoading || !run) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Loading workflow details…</p>
      </Card>
    );
  }

  const statusLabel: Record<string, string> = {
    completed: 'Completed',
    failed: 'Failed',
    running: 'Running',
    rolled_back: 'Rolled Back',
  };

  const statusVariant: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    completed: 'default',
    failed: 'destructive',
    running: 'secondary',
    rolled_back: 'outline',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to History
          </Button>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Workflow Report — Version {run.version}
            </h2>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {formatDateTime(run.started_at ?? run.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Database className="h-3.5 w-3.5" />
                Source: {run.input_source === 'crm' ? 'CRM Integration' : 'Replayed from previous run'}
              </span>
              <span>{run.records_processed ?? 0} records processed</span>
            </div>
          </div>
          <Badge variant={statusVariant[run.status] || 'secondary'} className="text-sm px-3 py-1">
            {statusLabel[run.status] || run.status}
          </Badge>
        </div>

        {run.error_logs && (run.error_logs as any[]).length > 0 && (
          <div className="mt-4 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
            <p className="text-sm font-medium text-destructive mb-1">Errors during this run:</p>
            {(run.error_logs as any[]).map((log: any, i: number) => (
              <p key={i} className="text-sm text-destructive/80">
                Step: {log.step} — {log.error}
              </p>
            ))}
          </div>
        )}
      </Card>

      {/* Tabbed Inspector */}
      <Card className="p-6">
        <Tabs defaultValue="raw" className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-4">
            <TabsTrigger value="raw" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Database className="h-3.5 w-3.5" /> Raw Data
            </TabsTrigger>
            <TabsTrigger value="cleaned" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Sparkles className="h-3.5 w-3.5" /> Cleaned Data
            </TabsTrigger>
            <TabsTrigger value="predictions" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <BarChart3 className="h-3.5 w-3.5" /> Predictions
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="files" className="flex items-center gap-1.5 text-xs sm:text-sm">
              <Download className="h-3.5 w-3.5" /> Files
            </TabsTrigger>
          </TabsList>

          <TabsContent value="raw">
            <WorkflowDataViewer runId={runId} dataType="raw" />
          </TabsContent>
          <TabsContent value="cleaned">
            <WorkflowCleaningReport runId={runId} />
          </TabsContent>
          <TabsContent value="predictions">
            <WorkflowPredictionViewer runId={runId} />
          </TabsContent>
          <TabsContent value="dashboard">
            <WorkflowDashboardTab run={run} />
          </TabsContent>
          <TabsContent value="files">
            <WorkflowOutputFiles runId={runId} runVersion={run.version} />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};
